import { generatedApi } from './api.generated';
import {
  createEndpointExports,
  createHookExports,
  provideListTags,
  LIST_TAG_ID,
} from '@skynet/data-helpers';
import type { HookExports } from '@skynet/data-helpers';
import { DEX_TASKS_API_ENDPOINTS as cfg } from './config';

/**
 * Cache tags follow the recommended `id: 'LIST'` recipe:
 * https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#recipes
 *
 * A single `DexTasks` tag type is used for both the collection and individual
 * tasks. The list query provides a per-item tag for every task plus a dedicated
 * `{ type: 'DexTasks', id: 'LIST' }` tag, so that:
 * - updating/deleting a single task invalidates only that task's `{ id }` tag,
 *   which refetches both the affected `getDexTask` query and the list (the list
 *   provides that same per-item tag);
 * - creating a task invalidates `{ id: 'LIST' }` to refetch the collection
 *   without touching unrelated tasks.
 *
 * The list response is paginated/wrapped (`{ status, data: { total, data: [] } }`),
 * so per-item tags are mapped over `result.data.data`, guarded for the
 * undefined-result case.
 *
 * This replaces a previous manual tag setup that lumped every task under a single
 * collection tag (and any `onQueryStarted` invalidation workaround attributed to
 * redux-toolkit#5093): that issue is about the `isSuccess` flag during pessimistic
 * updates, not tag invalidation — `invalidatesTags` propagates to the list
 * correctly via the per-item tags.
 */
const enhancedDexTasksApi = generatedApi.enhanceEndpoints({
  addTagTypes: ['DexTasks'],
  endpoints: {
    [cfg.createDexTask.generated]: {
      invalidatesTags: [{ type: 'DexTasks', id: LIST_TAG_ID }],
    },
    [cfg.listDexTasks.generated]: {
      providesTags: (result) => provideListTags(result?.data.data, 'DexTasks'),
    },
    [cfg.getDexTask.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'DexTasks', id: arg.taskId }],
    },
    [cfg.updateDexTask.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'DexTasks', id: arg.taskId }],
    },
    [cfg.updateDexTaskStatus.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'DexTasks', id: arg.taskId }],
    },
    [cfg.deleteDexTask.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'DexTasks', id: arg.taskId }],
    },
  },
});

export const dexTasksApi: typeof enhancedDexTasksApi = enhancedDexTasksApi;

export const hookExports: HookExports<typeof cfg, typeof dexTasksApi> = createHookExports(
  cfg,
  dexTasksApi,
);

const endpointExports = createEndpointExports(cfg, dexTasksApi);

export const dexTasksApiEndpoints: typeof endpointExports = endpointExports;
